import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ContentPage, waLink } from "@/components/site/ContentPage";
import { pageHead, SITE_URL } from "@/lib/seo";

const TITLE = "Consultório de cirurgia plástica em Foz do Iguaçu";
const DESCRIPTION =
  "Conheça o consultório do Dr. Kelson Kawamura no centro de Foz do Iguaçu, com estacionamento privativo, sala de espera confortável e fácil acesso.";
const PATH = "/consultorio-cirurgia-plastica-foz-do-iguacu";

const MAPS_ROUTE =
  "https://www.google.com/maps/dir/?api=1&destination=Rua+Marechal+Floriano+Peixoto%2C+960+-+Foz+do+Igua%C3%A7u%2C+PR%2C+85851-020";

export const Route = createFileRoute("/consultorio-cirurgia-plastica-foz-do-iguacu")({
  head: () => pageHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: ConsultorioPage,
});

const FAQ = [
  {
    q: "Onde fica o consultório?",
    a: "Na Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, sala 86, no centro de Foz do Iguaçu.",
  },
  { q: "O local tem estacionamento?", a: "O edifício conta com estacionamento privativo." },
  {
    q: "O consultório atende pacientes de outras cidades?",
    a: "Sim. O atendimento contempla pacientes de Cascavel, Ciudad del Este e outras cidades da região.",
  },
  {
    q: "Qual é o horário de atendimento?",
    a: "De segunda a sexta, das 08h às 12h e das 14h às 18h, e aos sábados das 08h às 12h.",
  },
  { q: "Como agendar?", a: "O agendamento pode ser feito pelo WhatsApp ou pelos canais de contato do site." },
];

function ConsultorioPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Consultório Dr. Kelson Kawamura - Cirurgia Plástica",
    url: `${SITE_URL}${PATH}`,
    telephone: "+55 45 99917-5813",
    email: "drkelsonkawamura@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, sala 86",
      addressLocality: "Foz do Iguaçu",
      addressRegion: "PR",
      postalCode: "85851-020",
      addressCountry: "BR",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00",
      },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "08:00", closes: "12:00" },
    ],
    areaServed: ["Foz do Iguaçu", "Cascavel", "Ciudad del Este", "Região Oeste do Paraná"],
  };

  const extra = (
    <div className="kk-info-grid" id="contato">
      <div className="kk-info-card">
        <h2 className="kk-info-card__title">Contato e horários</h2>
        <ul className="kk-contact-list" style={{ marginTop: 0 }}>
          <li>
            <span className="kk-contact-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 1.1 4.2 2 2 0 0 1 3.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.4 2.1L7.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.7 2z" />
              </svg>
            </span>
            <span>
              WhatsApp e telefone
              <br />
              <a href="tel:+5545999175813">+55 45 99917-5813</a>
            </span>
          </li>
          <li>
            <span className="kk-contact-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
            </span>
            <span>
              E-mail
              <br />
              <a href="mailto:drkelsonkawamura@gmail.com">drkelsonkawamura@gmail.com</a>
            </span>
          </li>
          <li>
            <span className="kk-contact-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
              </svg>
            </span>
            <span>
              Instagram
              <br />
              <a href="https://instagram.com/drkelsonkawamura" target="_blank" rel="noopener noreferrer">
                @drkelsonkawamura
              </a>
            </span>
          </li>
          <li>
            <span className="kk-contact-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </span>
            <span>
              Segunda a sexta: 08h às 12h e 14h às 18h
              <br />
              Sábado: 08h às 12h
            </span>
          </li>
        </ul>
      </div>


      <div className="kk-info-card">
        <h2 className="kk-info-card__title">Estrutura e experiência</h2>
        <ul className="kk-bullets" style={{ margin: 0 }}>
          <li>Localização central.</li>
          <li>Estacionamento privativo.</li>
          <li>Sala de espera confortável.</li>
          <li>Ambiente planejado para privacidade e acolhimento.</li>
          <li>Acesso facilitado para pacientes da cidade e da região.</li>
        </ul>
      </div>

    </div>
  );

  const leadExtra = (
    <p className="kk-lead">
      O atendimento contempla pacientes de Foz do Iguaçu, Cascavel, Ciudad del Este e outras cidades em um raio
      aproximado de 200 quilômetros, com orientação sobre endereço, horários e organização da consulta para quem vem
      de fora.
    </p>
  );

  const heroAside = (
    <div>
      <div className="kk-hero-map">
        <iframe
          title="Mapa do consultório do Dr. Kelson Kawamura em Foz do Iguaçu"
          src="https://www.google.com/maps?q=Rua%20Marechal%20Floriano%20Peixoto%2C%20960%2C%20Foz%20do%20Igua%C3%A7u%2C%20PR&output=embed"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
        <div className="kk-hero-map__address">
          <strong>Endereço</strong>
          Rua Marechal Floriano Peixoto, 960 — Edifício Torre Marechal, sala 86
          <br />
          Centro — Foz do Iguaçu, PR — CEP 85851-020
        </div>
      </div>
      <a href={MAPS_ROUTE} target="_blank" rel="noopener noreferrer" className="kk-button-secondary" style={{ marginTop: 20 }}>
        Traçar rota
      </a>
    </div>
  );

  return (
    <SiteShell>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <ContentPage
        hideSideNav
        heroAside={heroAside}
        leadExtra={leadExtra}
        eyebrow="Localização e confiança"
        h1="Consultório de cirurgia plástica em Foz do Iguaçu"
        heroTitle="Atendimento no centro de Foz do Iguaçu"
        lead="O consultório do Dr. Kelson está localizado em uma região central, em um dos edifícios mais modernos do Paraná, com estacionamento privativo, sala de espera confortável e estrutura pensada para oferecer praticidade, segurança e tranquilidade."
        crumbs={[{ label: "Consultório" }]}
        ctaLabel="Falar com a equipe"
        ctaHref={waLink("Olá! Gostaria de informações sobre o consultório do Dr. Kelson Kawamura.")}
        
        blocks={[]}
        extra={extra}
        faq={FAQ}
      />
    </SiteShell>
  );
}
